import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../interfaces/RootState";
import React, { useEffect, useMemo } from "react";
import {
    FetchCompanyContractsAction,
    UpdateCompanyFrameworkContractAction,
} from "../../../redux/contracts/contracts-actions";
import { Company } from "../../../redux/companies/companies-types";
import { CompanyContracts, FrameworkContract } from "../../../redux/contracts/contracts-types";
import { companyContractsViewData } from "../../contracts/utils";
import { CompanyContractsTable } from "./CompanyContractsTable";

export type Props = {
    /**
     * Redux dispatch function.
     */
    dispatch: AppDispatch;

    /**
     * Company whose contracts are displayed.
     */
    company: Company;
};

/**
 *  View current status for contracts of a company, along with buttons to
 *  toggle the framework contract coverage on and off.
 */
export const CompanyContractsView: React.FC<Props> = ({ dispatch, company }: Props) => {
    const { t } = useTranslation();

    const companyId = company.id;

    useEffect(() => {
        if (companyId !== 0) {
            dispatch(FetchCompanyContractsAction({ companyId }));
        }
    }, [dispatch, companyId]);

    const companyContracts: CompanyContracts = useSelector(
        (state: RootState) =>
            state.hydrolink.contracts.companyContractsByCompanyId[company.id] ?? {
                frameworkContract: {
                    updatedAt: null,
                    coversSubscriptionContract: false,
                    coversHousingCompanyDpa: false,
                    coversManagementCompanyDpa: false,
                    companyId: companyId,
                },

                companySubscriptionContracts: [],
                companyDpaContracts: [],
                userDpaContracts: [],
            },
    );

    const viewData = useMemo(() => companyContractsViewData(companyContracts), [companyContracts]);

    const frameworkContract: FrameworkContract = useMemo(() => {
        return (
            companyContracts.frameworkContract ?? {
                coversSubscriptionContract: false,
                coversHousingCompanyDpa: false,
                coversManagementCompanyDpa: false,
                companyId: companyId,
                updatedAt: null,
            }
        );
    }, [companyContracts, companyId]);

    /**
     * Action to perform when the switch is toggled.
     */
    const toggleFrameworkContract = (attr: keyof FrameworkContract, toggle: boolean) => {
        dispatch(
            UpdateCompanyFrameworkContractAction({
                ...frameworkContract,
                [attr]: toggle,
            }),
        );
    };

    return (
        <>
            <CompanyContractsTable
                rows={[
                    {
                        key: 1,
                        type: t("contracts.types.companySubscription"),
                        contractCoverage: viewData.subscriptionCoverage,
                        switchAction: (toggle) => toggleFrameworkContract("coversSubscriptionContract", toggle),
                        frameworkAgreementOn: frameworkContract.coversSubscriptionContract,
                    },
                    {
                        key: 2,
                        type: t("contracts.types.companyDpa"),
                        contractCoverage: viewData.dpaCoverage,
                        switchAction: (toggle) => toggleFrameworkContract("coversHousingCompanyDpa", toggle),
                        frameworkAgreementOn: frameworkContract.coversHousingCompanyDpa,
                    },
                    {
                        key: 3,
                        type: t("contracts.types.principalManagerDpa"),
                        contractCoverage: viewData.managementCompanyDpaCoverage,
                        switchAction: (toggle) => toggleFrameworkContract("coversManagementCompanyDpa", toggle),
                        frameworkAgreementOn: frameworkContract.coversManagementCompanyDpa,
                    },
                ]}
            />
        </>
    );
};
